import { useEffect, useRef, useState } from 'react'

import { Icon, TextButton } from '@joor/design-system'
import styled from 'styled-components/macro'

import { Attachment } from '__generated__/atlas-types'

import { useTriggerOrderPdfExportTasks } from '../DownloadDrawer/downloadDrawer.mutations'
import { useListOrderExportTasks } from '../DownloadDrawer/downloadDrawer.queries'
import FileUploader, {
  StyledLabel,
} from 'features/Orders/components/FileUploader/FileUploader'
import InvoiceLoadingLabel from 'features/Orders/components/InvoiceLoadingLabel/InvoiceLoadingLabel'
import { sendPaymentRequestIds } from 'features/Orders/orders.ids'

const FILE_CONTENT_PARAM = 'response-content-disposition'
const FILE_NAME_PARAM = 'filename='
const INVOICE_POLLING_TIME = 5000
const INVOICE_TIMEOUT_TO_CANCEL = 30000

const FileUploaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const AttachmentLink = styled.a`
  color: var(--color-secondary-800);
  max-width: 50%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AttachmentWrapper = styled.div`
  display: flex;
  gap: 5px;
`

const AttachmentLinks = styled.div`
  display: flex;
  gap: 4px;
  max-height: 16px;
`

const JoorPayInvoiceAttachContainer = styled.div`
  padding-left: 4px;
  border-left: 1px solid var(--color-primary-200);
`

interface InvoiceAttachmentProps {
  orderId?: string
  attachment?: Attachment
  attachmentLoading?: boolean
  handleUploading: (attachment: Attachment) => void
  handleUpdateAttachmentLoading: (attachmentLoading: boolean) => void
  onDeleteAttachment: () => void
  dueDate: string
}

export const InvoiceAttachment = ({
  orderId,
  attachment,
  attachmentLoading,
  handleUploading,
  handleUpdateAttachmentLoading,
  onDeleteAttachment,
  dueDate,
}: InvoiceAttachmentProps) => {
  const [tasksIds, setTasksIds] = useState<string[]>([])
  const [isTakingMoreTime, setIsTakingMoreTime] = useState<boolean>(false)
  const { data, startPolling, stopPolling } = useListOrderExportTasks(tasksIds)
  const { triggerOrderPdfExportTasks } = useTriggerOrderPdfExportTasks()
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()

  const handleGenerateJoorPayInvoice = async () => {
    if (orderId && dueDate) {
      handleUpdateAttachmentLoading(true)
      const response = await triggerOrderPdfExportTasks(orderId, dueDate)
      if (response && response?.data?.triggerOrdersPdfExportTasks.taskIds) {
        setTasksIds(response.data.triggerOrdersPdfExportTasks.taskIds)
        startPolling(INVOICE_POLLING_TIME)
        timeoutRef.current = setTimeout(() => {
          setIsTakingMoreTime(true)
        }, INVOICE_TIMEOUT_TO_CANCEL)
      }
    }
  }

  const handleStopPolling = () => {
    stopPolling()
    handleUpdateAttachmentLoading(false)
    setIsTakingMoreTime(false)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
  }

  useEffect(() => {
    if (data?.listOrderExportTasks?.exportTasks?.[0]?.downloadUrl) {
      const downloadUrl =
        data?.listOrderExportTasks?.exportTasks?.[0]?.downloadUrl
      const pdfParams = new URLSearchParams(downloadUrl.split('?')[1])
      const fileName = pdfParams
        .get(FILE_CONTENT_PARAM)
        ?.split(FILE_NAME_PARAM)[1]

      handleStopPolling()
      handleUploading({
        url: downloadUrl ?? '',
        fileName: fileName ?? '',
      })
    }
  }, [data?.listOrderExportTasks?.exportTasks])

  return (
    <FileUploaderWrapper>
      {attachment ? (
        <AttachmentWrapper>
          <AttachmentLink
            key={attachment?.url}
            href={attachment?.url}
            target="_blank"
          >
            {attachment?.fileName}
          </AttachmentLink>
          <TextButton className="notranslate" onClick={onDeleteAttachment}>
            x
          </TextButton>
        </AttachmentWrapper>
      ) : (
        <AttachmentLinks>
          <FileUploader
            customAttachLabel="Attach External Invoice"
            attachmentLoading={attachmentLoading}
            handleUploading={handleUploading}
          />

          <JoorPayInvoiceAttachContainer>
            <StyledLabel
              id={sendPaymentRequestIds.attachJoorInvoice}
              onClick={
                !attachmentLoading ? handleGenerateJoorPayInvoice : undefined
              }
              disabled={!!attachmentLoading}
            >
              <Icon iconName="attachment" plain iconSize="small" />
              Attach JOOR Invoice
            </StyledLabel>
          </JoorPayInvoiceAttachContainer>
        </AttachmentLinks>
      )}
      {attachmentLoading && (
        <InvoiceLoadingLabel
          isTakingMoreTime={isTakingMoreTime}
          handleStopPolling={handleStopPolling}
        />
      )}
    </FileUploaderWrapper>
  )
}
